import * as React from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { Check } from "@mui/icons-material";
import {useEffect, useRef} from "react";
import {useNavigate} from "react-router-dom";
import {NavigationService} from "../../services/NavigationService";
import {transformToNavigation} from "../../views/GettingData/GettingData";
import OptionalStepsModal from "../Modals/OptionalStepsModal";
import {LogsServices} from "../../services/LogsServices";

const QontoStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  color: theme.palette.mode === "dark" ? theme.palette.grey[700] : "#eaeaf0",
  display: "flex",
  height: 22,
  alignItems: "center",
  ...(ownerState.active && {
    color: "#047857", // Emerald-700
  }),
  "& .QontoStepIcon-completedIcon": {
    color: "#047857", // Emerald-700
    zIndex: 1,
    fontSize: 18,
  },
  "& .QontoStepIcon-circle": {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
}));

function QontoStepIcon(props) {
  const { active, completed, className } = props;

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <Check className="QontoStepIcon-completedIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

QontoStepIcon.propTypes = {
  active: PropTypes.bool,
  className: PropTypes.string,
  completed: PropTypes.bool,
};

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: "#91C8AA",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: "#91C8AA",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 5,
    border: 10,
    margin: "2px 10px 10px 10px",
    backgroundColor:
      theme.palette.mode === "dark" ? theme.palette.grey[800] : "#eaeaf0",
    borderRadius: 10,
  },
}));

const ColorlibStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  backgroundColor: ownerState.completed
    ? theme.palette.mode === "dark"
      ? theme.palette.grey[700]
      : "#ccc"
    : ownerState.unlocked
    ? "#9AE6B4" // Light green for unlocked steps (Emerald-300 equivalent)
    : theme.palette.mode === "dark"
    ? theme.palette.grey[700]
    : "#ccc", // Default grey
  zIndex: 1,
  color: "#fff",
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  cursor: ownerState.unlocked ? "pointer" : "default", // Add cursor pointer for unlocked steps
  ...(ownerState.active && {
    boxShadow: "0 3px 10px 0 rgba(0,0,0,.25)",
    width: 55,
    height: 55
  }),
  ...(ownerState.completed && ownerState.active ? {
    backgroundColor: "#227247",
  } : {
    backgroundColor: "#368C5D"
    }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, unlocked, className } = props;

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active, unlocked }}
      className={className}
    >
      {completed ? (
        <CheckCircleIcon />
      ) : unlocked ? (
        <span>{props.icon}</span>
      ) : (
        <div className="QontoStepIcon-circle">{props.icon}</div>
      )}
    </ColorlibStepIconRoot>
  );
}

ColorlibStepIcon.propTypes = {
  active: PropTypes.bool,
  className: PropTypes.string,
  completed: PropTypes.bool,
  unlocked: PropTypes.bool,
  icon: PropTypes.node,
};

export default function CustomizedSteppers({ navigation, setNavigation, activeStep, setActiveStep }) {

  const stepIndexRetrieved = useRef(false);
  const navigate = useNavigate();

  const notOptionalSteps = navigation.filter((step) => !step.optional);
  const optionalSteps = navigation.filter((step) => step.optional);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const lastCompletedIndex = notOptionalSteps
    .map((step, index) => (step.unlocked ? index : -1))
    .filter((i) => i !== -1)
    .pop() ?? 0;

  const calculatedActiveStep = activeStep >= notOptionalSteps.length
    ? lastCompletedIndex
    : activeStep;

  const openOptionalStepsModal = () => {
    setIsModalOpen(true);
  };

  const closeOptionalStepsModal = () => {
    setIsModalOpen(false);
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiResponse = await NavigationService.getNavigationSteps();
        const transformedNavigation = transformToNavigation(apiResponse);
        setNavigation(transformedNavigation);
        const lastUnlockedStepIndex = transformedNavigation.reduce(
          (lastIndex, step, index) => (step.unlocked && !step.optional ? index : lastIndex),
          0
        );

        if (!stepIndexRetrieved.current) {
          const storedStepIndex = localStorage.getItem("stepIndex");

          if (storedStepIndex !== null) {
            setActiveStep(Number(storedStepIndex));
            localStorage.removeItem("stepIndex");
          } else {
            setActiveStep(lastUnlockedStepIndex);
          }

          stepIndexRetrieved.current = true;
        }
      } catch (error) {
        console.error("Erro ao obter os passos de navegação:", error);
      }
    };

    fetchData();
  }, [setNavigation, setActiveStep]);

  const onStepClick = (stepIndex) => {
    if (navigation[stepIndex]?.unlocked || navigation[stepIndex]?.optional) {
      LogsServices.postLog(`Navegação: Troca para a etapa ${navigation[stepIndex].stepName}`);
      const url = window.location.pathname;

      if(url === "/dashboard") {
        localStorage.setItem("stepIndex", stepIndex);
        navigate("/getting-data");
      } else {
        setActiveStep(stepIndex);
      }
    }
  };

  return (
    <>
      <div className="w-full flex justify-center pt-2">
        <div className="flex overflow-x-auto pb-4 scrollbar-corner-emerald-500 scrollbar-thin scrollbar-thumb-emerald-700 scrollbar-track-slate-300">
          <Stack sx={{ width: "max-content" }} spacing={4}>
            <Stepper
              alternativeLabel
              activeStep={calculatedActiveStep}
              connector={<ColorlibConnector />}
            >
              {notOptionalSteps.map((step, index) => (
                <Step key={step.stepName} onClick={() => onStepClick(index)} sx={{minWidth: "95px"}}>
                  <StepLabel
                    StepIconComponent={(props) => (
                      <ColorlibStepIcon
                        {...props}
                        icon={index + 1}
                        completed={step.completed}
                        unlocked={step.unlocked}
                        active={calculatedActiveStep === index}

                      />
                    )}
                  >
                    {activeStep === index ? <span className="font-semibold text-gray-600">{step.stepName}</span> : <span className="font-medium">{step.stepName}</span>}
                  </StepLabel>
                </Step>
              ))}
              {optionalSteps.length > 0 && (
                <Step
                  onClick={() => notOptionalSteps.length > 0 && notOptionalSteps[notOptionalSteps.length-1].completed && openOptionalStepsModal()}
                  sx={{ '& .MuiStepConnector-line': { display: 'none' } }}
                >
                  <StepLabel
                    StepIconComponent={(props) => (
                      <ColorlibStepIcon
                        {...props}
                        icon="+"
                        completed={false}
                        unlocked={!!notOptionalSteps[notOptionalSteps.length-1].completed}
                        active={!!notOptionalSteps[notOptionalSteps.length-1].completed}
                      />
                    )}
                    sx={{
                      '& .MuiStepLabel-labelContainer': {
                        color: notOptionalSteps[notOptionalSteps.length - 1].completed
                          ? 'rgba(0, 0, 0, 1)'
                          : 'rgba(0, 0, 0, 0.6)'
                      }
                    }}
                  >
                    Etapas opcionais
                  </StepLabel>
                </Step>
              )}
            </Stepper>
          </Stack>
        </div>
      </div>

      {isModalOpen && (
        <OptionalStepsModal optionalSteps={optionalSteps} closeOptionalStepsModal={closeOptionalStepsModal} onStepClick={onStepClick} numberOfNotOptionalSteps={notOptionalSteps.length} />
      )}
    </>
  );
}
